// react
import DatePicker, { registerLocale } from 'react-datepicker';

import { da } from 'date-fns/locale/da';
// Shared DatePicker utilities and constants
import { DateTime } from 'luxon';

// Register Danish locale
registerLocale('da', da);

// Timezone constant
export const ZONE = 'Europe/Copenhagen';

// Format date to Danish format
export const formatDate = (date) => {
  if (!date) return '';
  const day = date.setLocale('da').toFormat('d.');
  const month = date.setLocale('da').toFormat('MMMM');
  const year = date.setLocale('da').toFormat('yyyy');
  return `${day} ${month} ${year}`;
};

// Normalize date range to timezone
export const normalizeRangeToZone = (range) => {
  if (!range) return [null, null];
  const [start, end] = range;
  return [start ? start.setZone(ZONE) : null, end ? end.setZone(ZONE) : null];
};

// Shortcut items for date selection
export const shortcutsItems = [
  {
    label: '2026',
    getValue: () => {
      const now = DateTime.now().setZone(ZONE);
      return [now.startOf('year'), now];
    },
  },
  {
    label: '2025',
    getValue: () => {
      const now = DateTime.now().setZone(ZONE);
      const lastYearStart = now.minus({ years: 1 }).startOf('year');
      const lastYearEnd = now.minus({ years: 1 }).endOf('year');
      return [lastYearStart, lastYearEnd];
    },
  },
  {
    label: 'Sidste 7 dage',
    getValue: () => {
      const now = DateTime.now().setZone(ZONE);
      return [
        now.minus({ days: 7 }).startOf('day'),
        now.minus({ days: 1 }).endOf('day'),
      ];
    },
  },
  {
    label: 'Sidste 4 uger',
    getValue: () => {
      const now = DateTime.now().setZone(ZONE);
      return [
        now.minus({ weeks: 4 }).startOf('day'),
        now.minus({ days: 1 }).endOf('day'),
      ];
    },
  },
];

// Parse different timestamp formats to DateTime
export const parseTimestamp = (timestamp) => {
  if (!timestamp) return null;

  // Handle DateTime objects
  if (timestamp instanceof DateTime) {
    return timestamp.setZone(ZONE);
  }

  // Handle JavaScript Date objects
  if (timestamp instanceof Date) {
    return DateTime.fromJSDate(timestamp).setZone(ZONE);
  }

  // Handle string timestamps
  if (typeof timestamp === 'string') {
    // Try ISO format first (most common)
    if (timestamp.includes('T') || timestamp.includes('-')) {
      const dt = DateTime.fromISO(timestamp);
      if (dt.isValid) return dt.setZone(ZONE);
    }

    // Try as Unix timestamp in milliseconds (13 digits)
    if (timestamp.length === 13 && /^\d+$/.test(timestamp)) {
      const dt = DateTime.fromMillis(parseInt(timestamp));
      if (dt.isValid) return dt.setZone(ZONE);
    }

    // Try as Unix timestamp in seconds (10 digits)
    if (timestamp.length === 10 && /^\d+$/.test(timestamp)) {
      const dt = DateTime.fromSeconds(parseInt(timestamp));
      if (dt.isValid) return dt.setZone(ZONE);
    }
  }

  // Handle numeric timestamps
  if (typeof timestamp === 'number') {
    // Assume milliseconds if > 1e12, otherwise seconds
    const dt =
      timestamp > 1e12
        ? DateTime.fromMillis(timestamp)
        : DateTime.fromSeconds(timestamp);
    if (dt.isValid) return dt.setZone(ZONE);
  }

  console.warn('Unable to parse timestamp:', timestamp);
  return null;
};

// Re-export DatePicker for convenience
export { DatePicker };
